/**
 * Reading and showing a memory amount (spec 149 FR-003, FR-012).
 *
 * The two halves of one unit convention live here: `parseMemoryLimit` turns what
 * somebody typed into MiB, `formatMemory` turns MiB back into words. The 1024 factor
 * appears in this module and nowhere else.
 */

import type { MemoryLimitViewUnit } from '@/lib/api/generated/model';

/** The unit the service reports and accepts; every generated memory schema carries this one enum. */
export type MemoryUnit = MemoryLimitViewUnit;

export type MemoryLimitInput =
  | { readonly ok: true; readonly value_mib: number; readonly unit: MemoryUnit }
  | { readonly ok: false; readonly reason: string };

const MIB_PER_GIB = 1024;
const DIGITS = /^\d+$/;

/**
 * A whole number of `unit`, checked against the minimum the service reported (never a
 * constant here). There is no maximum. Digits only, for the reason `parseConcurrencyLimit` gives:
 * `1.5`, `5e1` and `+3` are things `Number()` accepts and nobody meant as an amount.
 * The result is already in MiB, so no caller does arithmetic.
 */
export function parseMemoryLimit(
  input: string,
  unit: MemoryUnit,
  minimum: number,
): MemoryLimitInput {
  const trimmed = input.trim();
  const count = DIGITS.test(trimmed) ? Number(trimmed) : NaN;
  if (!Number.isSafeInteger(count)) {
    return { ok: false, reason: rangeReason(trimmed, minimum) };
  }
  const value_mib = unit === 'GiB' ? count * MIB_PER_GIB : count;
  if (value_mib < minimum) {
    return {
      ok: false,
      reason: rangeReason(String(value_mib), minimum),
    };
  }
  if (unit === 'GiB' && value_mib % MIB_PER_GIB !== 0) {
    return {
      ok: false,
      reason: `A memory limit entered in GiB must be a whole number of GiB; got ${value_mib} MiB.`,
    };
  }
  return { ok: true, value_mib, unit };
}

/** The service's own wording (`MEMORY_LIMIT_OUT_OF_RANGE`), so both refusals read as one voice. */
function rangeReason(got: string, minimum: number): string {
  return `A memory limit must be a whole number of MiB of at least ${minimum}; got ${got}.`;
}

/**
 * `6 GiB` or `6144 MiB`, in the unit it is given — never a fractional GiB, and never
 * GiB inferred from a multiple of 1024: the service reports `MiB` for fallback rows
 * and clamped reads, and the UI shows what was reported (FR-012b).
 */
export function formatMemory(value_mib: number, unit: MemoryUnit): string {
  return unit === 'GiB'
    ? `${Math.round(value_mib / MIB_PER_GIB)} GiB`
    : `${value_mib} MiB`;
}

/**
 * The unit to show an amount in when the service reported none — the minimum, a
 * fallback. Only for such figures: an amount that carries a reported unit is shown in
 * that unit, never in this one (FR-012b).
 */
export function unitFor(value_mib: number): MemoryUnit {
  return value_mib % MIB_PER_GIB === 0 ? 'GiB' : 'MiB';
}

/** The count of `unit` that makes `value_mib`, for seeding the amount field. */
export function countIn(value_mib: number, unit: MemoryUnit): number {
  return unit === 'GiB' ? Math.round(value_mib / MIB_PER_GIB) : value_mib;
}
